import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Radio, RadioGroup, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

interface Row {
  student_id: string
  name:       string
  email:      string
  status:     'present' | 'absent' | 'excused' | 'no_show' | ''
  /** True only for office hours: row only shown if student RSVP'd. */
  rsvpd:      boolean
}

interface Props {
  open:       boolean
  meetingId:  string | null
  classId:    string | null
  meetingKind: 'group' | 'office_hours' | null
  meetingLabel: string
  onClose:    () => void
  onSaved:    () => void
}

const GROUP_OPTIONS:   Array<Row['status']> = ['present', 'absent', 'excused', '']
const OFFICEH_OPTIONS: Array<Row['status']> = ['present', 'no_show', '']

const STATUS_LABEL: Record<string, string> = {
  '':         '\u2014',
  present:    'Present',
  absent:     'Absent',
  excused:    'Excused',
  no_show:    'No-show',
}

/* Radios, not a dropdown: marking a roster is one click per student, and a
   Select costs three (open, find, pick) plus a popup that hides the next row.
   Colour carries the meaning so a filled roster reads at a glance. */
const STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  present: 'success',
  absent:  'error',
  excused: 'warning',
  no_show: 'error',
  '':      'default',
}

/* '' last, as an escape hatch: once a radio is picked there is otherwise no
   way back to unmarked, and an unmarked row is simply not saved. */
function orderOptions(opts: Array<'present' | 'absent' | 'excused' | 'no_show' | ''>) {
  return [...opts.filter(o => o !== ''), ...opts.filter(o => o === '')]
}

export default function AttendanceDialog({
  open, meetingId, classId, meetingKind, meetingLabel, onClose, onSaved,
}: Props) {
  const [rows,       setRows]       = useState<Row[]>([])
  const [loading,    setLoading]    = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  useEffect(() => {
    if (!open || !meetingId || !classId || !meetingKind) return
    void load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meetingId, classId, meetingKind])

  async function load() {
    setLoading(true)
    setError(null)

    if (meetingKind === 'group') {
      // All active enrollments + any existing attendance for this meeting
      const [{ data: enrollments }, { data: attended }] = await Promise.all([
        supabase
          .from('class_enrollment')
          .select('student_id')
          .eq('class_id', classId!)
          .eq('status', 'active'),
        supabase
          .from('class_meeting_attendance')
          .select('student_id, status')
          .eq('meeting_id', meetingId!),
      ])
      const studentIds = (enrollments ?? []).map(e => e.student_id)
      const attMap: Record<string, string> = Object.fromEntries(
        (attended ?? []).map(a => [a.student_id, a.status])
      )
      const { data: profiles } = await supabase
        .from('student_profile')
        .select('student_id, user_id')
        .in('student_id', studentIds)
      const userIds = (profiles ?? []).map(p => p.user_id)
      const { data: users } = await supabase
        .from('users')
        .select('user_id, first_name, last_name, email')
        .in('user_id', userIds)
      const userMap: Record<string, { first_name: string; last_name: string; email: string }> =
        Object.fromEntries((users ?? []).map(u => [u.user_id, u]))
      const list: Row[] = (profiles ?? []).map(p => {
        const u = userMap[p.user_id] ?? { first_name: '', last_name: '', email: '' }
        const existing = attMap[p.student_id]
        const status: Row['status'] = (existing === 'present' || existing === 'absent' || existing === 'excused')
          ? existing
          : ''
        return {
          student_id: p.student_id,
          name:       `${u.first_name} ${u.last_name}`.trim() || u.email,
          email:      u.email,
          status,
          rsvpd:      true,    // not relevant for group
        }
      }).sort((a, b) => a.name.localeCompare(b.name))
      setRows(list)
    } else {
      // office_hours: only students who already have an attendance row
      const { data: attendance } = await supabase
        .from('class_meeting_attendance')
        .select('student_id, status')
        .eq('meeting_id', meetingId!)
      const studentIds = (attendance ?? []).map(a => a.student_id)
      if (studentIds.length === 0) { setRows([]); setLoading(false); return }
      const { data: profiles } = await supabase
        .from('student_profile')
        .select('student_id, user_id')
        .in('student_id', studentIds)
      const userIds = (profiles ?? []).map(p => p.user_id)
      const { data: users } = await supabase
        .from('users')
        .select('user_id, first_name, last_name, email')
        .in('user_id', userIds)
      const userMap: Record<string, { first_name: string; last_name: string; email: string }> =
        Object.fromEntries((users ?? []).map(u => [u.user_id, u]))
      const attMap: Record<string, string> = Object.fromEntries(
        (attendance ?? []).map(a => [a.student_id, a.status])
      )
      const list: Row[] = (profiles ?? []).map(p => {
        const u = userMap[p.user_id] ?? { first_name: '', last_name: '', email: '' }
        const existing = attMap[p.student_id]
        const status: Row['status'] = (existing === 'present' || existing === 'no_show')
          ? existing
          : ''
        return {
          student_id: p.student_id,
          name:       `${u.first_name} ${u.last_name}`.trim() || u.email,
          email:      u.email,
          status,
          rsvpd:      true,
        }
      }).sort((a, b) => a.name.localeCompare(b.name))
      setRows(list)
    }
    setLoading(false)
  }

  function setStatus(student_id: string, status: Row['status']) {
    setRows(prev => prev.map(r => r.student_id === student_id ? { ...r, status } : r))
  }

  async function handleSave() {
    if (!meetingId) return
    const toSave = rows.filter(r => r.status !== '')
    if (toSave.length === 0) {
      setError('Mark at least one student before saving.'); return
    }
    setSubmitting(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('mark-attendance', {
      body: {
        meeting_id: meetingId,
        entries:    toSave.map(r => ({ student_id: r.student_id, status: r.status })),
      },
    })
    setSubmitting(false)
    if (invokeErr) { setError(await readFunctionError(invokeErr)); return }
    if (data?.error) { setError(data.error); return }
    onSaved()
    onClose()
  }

  const options = meetingKind === 'office_hours' ? OFFICEH_OPTIONS : GROUP_OPTIONS

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Mark Attendance
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
          {meetingLabel}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            {meetingKind === 'office_hours'
              ? 'No students RSVPd to this office hour.'
              : 'No students enrolled in this class.'}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.student_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <RadioGroup
                      row
                      value={r.status}
                      onChange={e => setStatus(r.student_id, e.target.value as Row['status'])}
                      sx={{ flexWrap: 'nowrap', gap: 0.5 }}
                    >
                      {orderOptions(options).map(o => {
                        const control = (
                          <FormControlLabel
                            key={o || 'unset'}
                            value={o}
                            control={
                              <Radio
                                size="small"
                                color={STATUS_COLOR[o] === 'default' ? undefined : STATUS_COLOR[o]}
                                sx={{ py: 0.25 }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {STATUS_LABEL[o]}
                              </Typography>
                            }
                            sx={{ mr: 0.5 }}
                          />
                        )
                        /* The dash needs saying out loud; the others read
                           for themselves. */
                        return o === ''
                          ? <Tooltip key="unset" title="Not marked">{control}</Tooltip>
                          : control
                      })}
                    </RadioGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting || rows.length === 0}>
          {submitting ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
